/**
 * KAN-961: хранит заголовок текущей страницы для верхней строки кабинета.
 * Ставится в DashboardLayout над шапкой и содержимым.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  type PageHeaderCrumb,
  PageHeaderDataContext,
  PageHeaderSetterContext,
  type PageHeaderData,
  type PageHeaderSetter,
} from './pageHeaderContext';

type Entry = PageHeaderData & { owner: symbol };

function sameCrumbs(a: PageHeaderCrumb[] | undefined, b: PageHeaderCrumb[] | undefined): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every(
    (item, i) => item.label === b[i].label && item.href === b[i].href && item['data-testid'] === b[i]['data-testid']
  );
}

/**
 * Та же запись: страница перерисовалась без смены заголовка (ввод в фильтр,
 * опрос). Строка subtitle сравнивается по значению, элемент по ссылке.
 */
function sameEntry(a: Entry | null, b: Entry): boolean {
  return (
    a !== null &&
    a.owner === b.owner &&
    a.title === b.title &&
    a.subtitle === b.subtitle &&
    sameCrumbs(a.breadcrumbs, b.breadcrumbs)
  );
}

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [entry, setEntry] = useState<Entry | null>(null);

  // Без изменений возвращаем prev: верхняя строка не перерисовывается.
  const set = useCallback((next: Entry) => setEntry((prev) => (sameEntry(prev, next) ? prev : next)), []);
  const clear = useCallback(
    (owner: symbol) => setEntry((prev) => (prev && prev.owner === owner ? null : prev)),
    []
  );
  const setter = useMemo<PageHeaderSetter>(() => ({ set, clear }), [set, clear]);
  const data = useMemo<PageHeaderData | null>(
    () => (entry ? { title: entry.title, subtitle: entry.subtitle, breadcrumbs: entry.breadcrumbs } : null),
    [entry]
  );

  return (
    <PageHeaderSetterContext.Provider value={setter}>
      <PageHeaderDataContext.Provider value={data}>{children}</PageHeaderDataContext.Provider>
    </PageHeaderSetterContext.Provider>
  );
}
